import { SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectFeatureFlags } from 'core/userContext';
import { usePermissionsContext } from 'modules/roles/contexts';
import { MergedPermission } from 'modules/roles/types';
import {
  createCategories,
  createOrderedGroups,
  manageGroupCheckbox,
  manageSingleCheckbox,
} from 'modules/roles/utils';
import { Spinner } from 'ui/spinner';
import { spacing } from 'ui/theme';
import { Group } from './Group';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';

export const GroupsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${spacing.x3};
`;

type Props = {
  isLoading: boolean;
  permissions: number[];
  setPermissions: (value: SetStateAction<number[]>) => void;
};

export const Permissions = ({ isLoading, permissions, setPermissions }: Props) => {
  const { UseChannelBasedPermissions } = useSelector(selectFeatureFlags);
  const { getPermissionById } = usePermissionsContext();
  const isAssetManagerIndexFFEnabled = useFeatureFlag('AssetManagerIndex', false);

  const handleCheckboxChange = (id: number) => {
    setPermissions((prev: number[]) => {
      const current = getPermissionById(id);
      const { children = [], requires = [] } = current as MergedPermission;

      return children.length > 0
        ? manageGroupCheckbox(prev, children, getPermissionById)
        : manageSingleCheckbox(prev, id, requires);
    });
  };

  const orderedGroups = createOrderedGroups();

  return isLoading ? (
    <Spinner />
  ) : (
    <GroupsWrapper>
      {orderedGroups.map((group) => {
        const categories = createCategories(
          group.categories,
          UseChannelBasedPermissions,
          isAssetManagerIndexFFEnabled,
        );
        return (
          <Group
            getPermissionById={getPermissionById}
            id={group.id}
            key={group.id}
            name={group.name}
            categories={categories}
            handleChange={handleCheckboxChange}
            selectedPermissions={permissions}
          />
        );
      })}
    </GroupsWrapper>
  );
};
